import { trackDesignSystemAction } from 'analytics';
import forge from 'apis/forge';
import DsPicker from 'components/Widget/DsPicker';
import type { ShowcaseDesignSystem } from 'components/Modal/DsShowcaseModal';
import { DesignSystem } from 'dragonmouse-shared/interfaces';
import { fetchSceneDsLink } from 'designSystem/util';
import { useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { selectActiveScene, selectActiveSceneDsLink, selectDesignSystems } from 'redux/db/reducers';
import { canEdit } from 'redux/db/util';
import { showDsShowcaseModal } from 'redux/mode/actions';
import { RootReducer } from 'redux/reducers';
import { displayViewOnlyModal } from 'redux/mode/util';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import * as routes from 'routes';

const DsPickerWrapper = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const [showcaseLoading, setShowcaseLoading] = useState(false);
	const [showcaseLoadingTitle, setShowcaseLoadingTitle] = useState('');

	const { scene, designSystems, dsLink } = useSelector((r: RootReducer) => {
		return {
			scene: selectActiveScene(r.db, r.mode.activeSceneId),
			designSystems: selectDesignSystems(r.db),
			dsLink: selectActiveSceneDsLink(r.db),
		};
	}, shallowEqual);

	//----------------------------------------------------------------------
	// HANDLERS
	//----------------------------------------------------------------------

	const onSelect = async (designSystem: DesignSystem | null) => {
		if (!scene || !canEdit(scene)) {
			displayViewOnlyModal();
			return;
		}

		// Unlinking is always allowed
		if (!designSystem) {
			trackDesignSystemAction('unlink_from_scene');
			try {
				await forge.linkSceneDesignSystem({
					sceneId: scene.id,
					designSystemId: null,
				});
			} catch (error) {
				console.error('Failed to unlink design system:', error);
				toast.error('Failed to unlink design system. Please try again.');
			}
			return;
		}

		const dsLink = await fetchSceneDsLink(designSystem.id);
		if (!dsLink) return;

		trackDesignSystemAction('select_existing_ds', {
			dsId: designSystem.id,
			dsName: designSystem.title,
		});
		try {
			await forge.linkSceneDesignSystem({
				sceneId: scene.id,
				designSystemId: designSystem.id,
				designSystemVersionNumber: dsLink.linkedVersion.versionNumber,
			});
		} catch (error) {
			console.error('Failed to link design system:', error);
			toast.error('Failed to link design system. Please try again.');
		}
	};

	const onCreateNew = () => {
		if (!canEdit(scene)) {
			displayViewOnlyModal();
			return;
		}
		trackDesignSystemAction('click_add_ds');
		navigate(routes.createDesignSystem());
	};

	const onUpgrade = async () => {
		if (!scene || !dsLink) return;
		if (!canEdit(scene)) {
			displayViewOnlyModal();
			return;
		}
		// Re-link the same design system, which will update to the latest published version
		trackDesignSystemAction('upgrade_scene_version');
		try {
			await forge.linkSceneDesignSystem({
				sceneId: scene.id,
				designSystemId: dsLink.designSystem.id,
			});
		} catch (error) {
			console.error('Failed to upgrade design system:', error);
			toast.error('Failed to upgrade design system. Please try again.');
		}
	};

	const onShowcaseSelect = async (item: ShowcaseDesignSystem) => {
		if (!scene || !canEdit(scene)) {
			displayViewOnlyModal();
			return;
		}

		setShowcaseLoading(true);
		setShowcaseLoadingTitle(item.title);

		try {
			const dsLink = await fetchSceneDsLink(item.designSystemId);
			if (!dsLink) return;

			await forge.linkSceneDesignSystem({
				sceneId: scene.id,
				designSystemId: item.designSystemId,
				designSystemVersionNumber: dsLink.linkedVersion.versionNumber,
			});
		} catch (error) {
			console.error('Failed to add design system:', error);
			toast.error('Failed to add design system. Please try again.');
		} finally {
			setShowcaseLoading(false);
			setShowcaseLoadingTitle('');
		}
	};

	const onBrowseShowcase = () => {
		dispatch(showDsShowcaseModal({ onSelect: onShowcaseSelect }));
	};

	//----------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------

	return (
		<DsPicker
			selection={dsLink}
			designSystems={designSystems}
			onSelect={onSelect}
			onCreateNew={onCreateNew}
			onUpgrade={onUpgrade}
			onBrowseShowcase={onBrowseShowcase}
			isExternalLoading={showcaseLoading}
			externalLoadingLabel={showcaseLoadingTitle ? `Adding ${showcaseLoadingTitle}...` : undefined}
		/>
	);
};

export default DsPickerWrapper;
